import {
  API_VERSION_DEFAULTS,
  DEFAULT_API_CONFIG,
} from '~/common/apis/api.constants';
import type { ApiResponse } from '~/common/models/apis/apiResponse.model';
import { BDError } from '~/common/models/error.model';
import type { CamerasSetting } from '~/common/models/settings.model';
import {
  addAcceptLanguageHeader,
  addCSFleetIdHeader,
  addCSOrganizationIdHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makeGetPayloadCreator,
  makePatchPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';

import type { CamerasSettingsParams } from './camerasSettingsSlice.model';

export enum CamerasSettingsActionType {
  FETCH_SETTINGS = 'cameraSettings/fetchCamerasSettings',
  UPDATE_SETTINGS = 'camerasSettings/updateCamerasSettings',
}

const CONFIG = DEFAULT_API_CONFIG;

const fetchCamerasSettings = makeThunk(
  CamerasSettingsActionType.FETCH_SETTINGS,
  makeGetPayloadCreator<ApiResponse<CamerasSetting[]>, CamerasSettingsParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/peripherals/fleet-cameras/${API_VERSION_DEFAULTS.camerasSettingsGetAPI}/settings`,
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addCSFleetIdHeader(
          addCSOrganizationIdHeader(CONFIG, organizationsId),
          fleetsId
        ),
        state.profile.currentLocale
      ),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<CamerasSetting[]>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected fetch cameras settings response', {
        data: response,
      });
    },
  })
);

const updateCamerasSettings = makeThunk(
  CamerasSettingsActionType.UPDATE_SETTINGS,
  makePatchPayloadCreator<ApiResponse, CamerasSettingsParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/peripherals/fleet-cameras/${API_VERSION_DEFAULTS.camerasSettingsUpdateAPI}/settings`,
    axiosOptions: ({ organizationsId }, state) =>
      addAcceptLanguageHeader(
        addCSOrganizationIdHeader(CONFIG, organizationsId),
        state.profile.currentLocale
      ),
    argAdapter: ({ requestBody }) => ({
      requestBody,
    }),
  })
);

export const CAMERAS_SETTINGS_ACTIONS = {
  fetchCamerasSettings,
  updateCamerasSettings,
};
